import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { createCardTexture, getCtaAnchor } from '../../utils/canvasTextures';
import { getProductLink, hasProductLink, normalizeProductLink, openProductLink } from '../../utils/productLink';
import './ProductCarousel3D.css';

const THEME = 'dark';

const CARD_SIZE = {
  desktop: { w: 6.2, h: 3.9 },
  mobile: { w: 4.8, h: 3.0 },
};

const CAMERA = {
  desktop: { z: 16, fov: 60 },
  mobile: { z: 11.5, fov: 52 },
};

const ChevronLeft = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ProductCarousel3D = ({
  products,
  cardContents,
  autoRotate = true,
  radius,
  rotationSpeed = 0.003,
}) => {
  const mountRef = useRef(null);
  const ctaWrapRef = useRef(null);
  const ctaWorldPosRef = useRef(new THREE.Vector3());
  const ctaProjectedRef = useRef(new THREE.Vector3());
  const rendererRef = useRef(null);
  const composerRef = useRef(null);
  const cameraRef = useRef(null);
  const cardMeshesRef = useRef([]);
  const spotLightRef = useRef(null);
  const particlesRef = useRef(null);
  const mouseRef = useRef(new THREE.Vector2(0, 0));
  const rafRef = useRef(null);
  const radiusRef = useRef(radius);
  const autoRotateIntervalRef = useRef(null);
  const isVisibleRef = useRef(true);
  const isMobileRef = useRef(false);
  const isPageScrollingRef = useRef(false);
  const scrollIdleTimeoutRef = useRef(null);
  const interactionRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    dragging: false,
    axis: null,
    didSwipe: false,
  });

  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const stateRef = useRef({ isHovered, autoRotate, focusedIndex });
  useEffect(() => {
    stateRef.current = { isHovered, autoRotate, focusedIndex };
  });

  useEffect(() => {
    radiusRef.current = radius;
  }, [radius]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const handleChange = () => {
      setIsMobile(media.matches);
      isMobileRef.current = media.matches;
    };
    handleChange();
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    const markPageScrolling = () => {
      isPageScrollingRef.current = true;
      mouseRef.current.set(0, 0);
      setIsHovered(false);

      if (scrollIdleTimeoutRef.current) {
        clearTimeout(scrollIdleTimeoutRef.current);
      }
      scrollIdleTimeoutRef.current = setTimeout(() => {
        isPageScrollingRef.current = false;
      }, 180);
    };

    window.addEventListener('scroll', markPageScrolling, { passive: true });
    window.addEventListener('wheel', markPageScrolling, { passive: true });

    return () => {
      window.removeEventListener('scroll', markPageScrolling);
      window.removeEventListener('wheel', markPageScrolling);
      if (scrollIdleTimeoutRef.current) {
        clearTimeout(scrollIdleTimeoutRef.current);
      }
    };
  }, []);

  const count = products.length;

  const updateTextures = useCallback(
    (active) => {
      cardMeshesRef.current.forEach((mesh, index) => {
        const tex = createCardTexture(
          cardContents[index],
          index === active,
          isMobileRef.current,
          THEME
        );
        if (tex && mesh.material instanceof THREE.MeshStandardMaterial) {
          if (mesh.material.map) mesh.material.map.dispose();
          mesh.material.map = tex;
          mesh.material.emissiveMap = tex;
          mesh.material.needsUpdate = true;
        }
      });
    },
    [cardContents]
  );

  useEffect(() => {
    updateTextures(focusedIndex);
  }, [focusedIndex, updateTextures, isMobile]);

  useEffect(() => {
    if (autoRotateIntervalRef.current) {
      clearInterval(autoRotateIntervalRef.current);
    }
    if (autoRotate && !isHovered && !isMobile && count > 1) {
      autoRotateIntervalRef.current = setInterval(() => {
        // Skip advancing while the section is frozen (page scrolling) or
        // off-screen, otherwise the carousel "catches up" with a fast multi-step
        // spin once rendering resumes.
        if (isPageScrollingRef.current || !isVisibleRef.current) {
          return;
        }
        setFocusedIndex((prev) => (prev - 1 + count) % count);
      }, 5000);
    }
    return () => {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
      }
    };
  }, [autoRotate, isHovered, isMobile, count]);

  useEffect(() => {
    if (!mountRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.1 }
    );
    observer.observe(mountRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mountRef.current || count === 0) return undefined;

    const mount = mountRef.current;
    const mobile = isMobileRef.current;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      // Keep the last painted frame on the canvas when we pause rendering
      // (e.g. while the page is scrolling), so the section stays visible
      // instead of disappearing.
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    rendererRef.current = renderer;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();

    const cameraPreset = mobile ? CAMERA.mobile : CAMERA.desktop;
    const cardSize = mobile ? CARD_SIZE.mobile : CARD_SIZE.desktop;

    const camera = new THREE.PerspectiveCamera(
      cameraPreset.fov,
      mount.clientWidth / mount.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, cameraPreset.z);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(mount.clientWidth, mount.clientHeight),
      0.5,
      0.4,
      0.5
    );
    composer.addPass(bloom);
    composerRef.current = composer;

    const isDark = THEME === 'dark';
    scene.add(new THREE.AmbientLight(0xffffff, isDark ? 0.35 : 1.0));
    const dir = new THREE.DirectionalLight(0xffffff, isDark ? 1.4 : 2.6);
    dir.position.set(5, 8, 10);
    dir.castShadow = true;
    scene.add(dir);

    const spot = new THREE.SpotLight(0x6366f1, isDark ? 70 : 90, 32, Math.PI / 7, 0.4, 1.5);
    spot.castShadow = true;
    spotLightRef.current = spot;
    scene.add(spot);

    const n = count;
    cardMeshesRef.current = products.map((product, index) => {
      const tex = createCardTexture(cardContents[index], index === 0, mobile, THEME);
      const { w, h } = cardSize;
      const geo = new THREE.PlaneGeometry(w, h);
      const mat = new THREE.MeshStandardMaterial({
        map: tex || undefined,
        transparent: true,
        emissive: '#ffffff',
        emissiveMap: tex || undefined,
        emissiveIntensity: isDark ? 0.12 : 0.06,
        roughness: isDark ? 0.5 : 0.7,
        metalness: 0.1,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData = { index, productId: product.id };
      mesh.userData.currentAngle = (index / n) * Math.PI * 2 + Math.PI / 2;
      const r = radiusRef.current;
      mesh.position.set(
        Math.cos(mesh.userData.currentAngle) * r,
        0,
        Math.sin(mesh.userData.currentAngle) * r
      );
      scene.add(mesh);
      return mesh;
    });

    const particleCount = mobile ? 1800 : 5000;
    const pos = new Float32Array(particleCount * 3);
    const col = new Float32Array(particleCount * 3);
    const vel = new Float32Array(particleCount * 3);
    const base = new THREE.Color(0x6366f1);
    for (let i = 0; i < particleCount; i += 1) {
      pos.set(
        [(Math.random() - 0.5) * 120, (Math.random() - 0.5) * 80, (Math.random() - 0.5) * 80],
        i * 3
      );
      const c = base.clone().lerp(new THREE.Color(0xffffff), Math.random() * 0.5);
      col.set([c.r, c.g, c.b], i * 3);
      vel.set(
        [(Math.random() - 0.5) * 0.01, (Math.random() - 0.5) * 0.02, (Math.random() - 0.5) * 0.01],
        i * 3
      );
    }
    const pgeo = new THREE.BufferGeometry();
    pgeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    pgeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    pgeo.setAttribute('velocity', new THREE.BufferAttribute(vel, 3));
    particlesRef.current = new THREE.Points(
      pgeo,
      new THREE.PointsMaterial({
        size: 0.06,
        transparent: true,
        opacity: 0.45,
        vertexColors: true,
      })
    );
    scene.add(particlesRef.current);

    const clock = new THREE.Clock();

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      if (!isVisibleRef.current) return;

      // Freeze the scene while the page is scrolling so the browser can
      // dedicate the main thread / GPU to a smooth scroll.
      if (isPageScrollingRef.current) {
        if (clock.running) clock.stop();
        return;
      }
      if (!clock.running) clock.start();

      clock.getDelta();
      const elapsed = clock.getElapsedTime();
      const r = radiusRef.current;
      const focusedI = stateRef.current.focusedIndex;
      const numCards = count;
      const mobileNow = isMobileRef.current;
      const hovered =
        stateRef.current.isHovered && !mobileNow && !isPageScrollingRef.current;

      const targetCamX = hovered ? mouseRef.current.x * 1.5 : 0;
      const targetCamY = hovered ? mouseRef.current.y * 0.8 : 0;
      camera.position.x += (targetCamX - camera.position.x) * 0.03;
      camera.position.y += (targetCamY - camera.position.y) * 0.03;
      camera.lookAt(0, 0, 0);

      cardMeshesRef.current.forEach((mesh, j) => {
        const targetAngle = ((j - focusedI) / numCards) * Math.PI * 2 + Math.PI / 2;

        let diff = targetAngle - mesh.userData.currentAngle;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;

        mesh.userData.currentAngle += diff * 0.038;

        const isFocused = j === focusedI;
        const focusedScale = mobileNow ? 1.2 : 1.12;
        const targetScale = isFocused ? focusedScale : 1.0;
        mesh.scale.setScalar(THREE.MathUtils.lerp(mesh.scale.x, targetScale, 0.08));

        mesh.position.x = Math.cos(mesh.userData.currentAngle) * r;
        mesh.position.z = Math.sin(mesh.userData.currentAngle) * r;
        mesh.position.y = Math.sin(elapsed * 0.5 + j * 0.6) * 0.18;

        mesh.lookAt(camera.position);

        const sinA = Math.sin(mesh.userData.currentAngle);
        const sideOpacity = mobileNow ? 0.55 : 0.75;
        const opacity = isFocused
          ? 1.0
          : Math.max(0.08, (sinA * 0.5 + 0.5) * sideOpacity + 0.1);
        mesh.material.opacity = opacity;
      });

      if (particlesRef.current) {
        const p = particlesRef.current.geometry.attributes.position;
        const v = particlesRef.current.geometry.attributes.velocity;
        const pa = p.array;
        const va = v.array;
        for (let i = 0; i < pa.length; i += 3) {
          pa[i] += va[i];
          pa[i + 1] += va[i + 1];
          pa[i + 2] += va[i + 2];
          if (pa[i + 1] < -60) pa[i + 1] = 60;
          if (pa[i + 1] > 60) pa[i + 1] = -60;
          if (pa[i] < -60 || pa[i] > 60) va[i] *= -1;
          if (pa[i + 2] < -60 || pa[i + 2] > 60) va[i + 2] *= -1;
        }
        p.needsUpdate = true;
        particlesRef.current.rotation.y += rotationSpeed * 0.6;
      }

      const focused = cardMeshesRef.current[focusedI];
      if (spotLightRef.current && focused) {
        const tp = new THREE.Vector3();
        focused.getWorldPosition(tp);
        tp.y += 2.5;
        spotLightRef.current.position.lerp(tp, 0.1);
        spotLightRef.current.target = focused;
      }

      const ctaWrap = ctaWrapRef.current;
      const focusedProduct = products[focusedI];
      if (ctaWrap && focused && mount && camera) {
        if (!hasProductLink(focusedProduct)) {
          ctaWrap.style.visibility = 'hidden';
        } else {
          const size = isMobileRef.current ? CARD_SIZE.mobile : CARD_SIZE.desktop;
          const anchor = getCtaAnchor(isMobileRef.current);
          const worldPos = ctaWorldPosRef.current;
          worldPos.set(
            -size.w / 2 + anchor.u * size.w,
            size.h / 2 - anchor.vTop * size.h,
            0.02
          );
          focused.localToWorld(worldPos);

          const projected = ctaProjectedRef.current.copy(worldPos).project(camera);
          const isVisible = projected.z <= 1 && focused.material.opacity > 0.85;

          if (!isVisible) {
            ctaWrap.style.visibility = 'hidden';
          } else {
            const x = (projected.x * 0.5 + 0.5) * mount.clientWidth;
            const y = (-projected.y * 0.5 + 0.5) * mount.clientHeight;
            ctaWrap.style.visibility = 'visible';
            ctaWrap.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
          }
        }
      }

      composer.render();
    };
    animate();
    composer.render();

    const handleResize = () => {
      if (!mount || !camera || !renderer || !composer) return;
      const { clientWidth, clientHeight } = mount;
      if (!clientWidth || !clientHeight) return;
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth, clientHeight, false);
      composer.setSize(clientWidth, clientHeight);
    };

    handleResize();
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(mount);

    return () => {
      resizeObserver.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      cardMeshesRef.current.forEach((mesh) => {
        if (mesh.material.map) mesh.material.map.dispose();
        mesh.geometry.dispose();
        mesh.material.dispose();
      });
      particlesRef.current?.geometry.dispose();
      particlesRef.current?.material.dispose();
      composer.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [cardContents, count, isMobile, products, rotationSpeed]);

  const handleMouseMove = useCallback((event) => {
    if (!mountRef.current || interactionRef.current.dragging || isPageScrollingRef.current) return;
    const rect = mountRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }, []);

  const goToNext = useCallback(() => {
    setFocusedIndex((prev) => (prev + 1) % count);
  }, [count]);

  const goToPrev = useCallback(() => {
    setFocusedIndex((prev) => (prev - 1 + count) % count);
  }, [count]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || count <= 1 || isMobile) return undefined;

    const SWIPE_THRESHOLD = 48;
    const DRAG_START = 10;

    const resetInteraction = () => {
      interactionRef.current.pointerId = null;
      interactionRef.current.dragging = false;
      interactionRef.current.axis = null;
      setIsDragging(false);
    };

    const onPointerDown = (event) => {
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      interactionRef.current.pointerId = event.pointerId;
      interactionRef.current.startX = event.clientX;
      interactionRef.current.startY = event.clientY;
      interactionRef.current.dragging = false;
      interactionRef.current.axis = null;
      interactionRef.current.didSwipe = false;
      mount.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (interactionRef.current.pointerId !== event.pointerId) return;

      const dx = event.clientX - interactionRef.current.startX;
      const dy = event.clientY - interactionRef.current.startY;

      if (!interactionRef.current.dragging) {
        if (Math.abs(dx) > DRAG_START && Math.abs(dx) > Math.abs(dy) * 1.15) {
          interactionRef.current.dragging = true;
          interactionRef.current.axis = 'horizontal';
          setIsDragging(true);
        } else if (Math.abs(dy) > DRAG_START && Math.abs(dy) > Math.abs(dx) * 1.15) {
          interactionRef.current.axis = 'vertical';
        }
      }

      if (interactionRef.current.axis === 'horizontal' && event.cancelable) {
        event.preventDefault();
      }
    };

    const onPointerUp = (event) => {
      if (interactionRef.current.pointerId !== event.pointerId) return;

      const dx = event.clientX - interactionRef.current.startX;
      const dy = event.clientY - interactionRef.current.startY;
      const wasHorizontalDrag = interactionRef.current.axis === 'horizontal';

      if (mount.hasPointerCapture(event.pointerId)) {
        mount.releasePointerCapture(event.pointerId);
      }

      if (wasHorizontalDrag && Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        interactionRef.current.didSwipe = true;
        if (dx < 0) {
          goToNext();
        } else {
          goToPrev();
        }
      }

      resetInteraction();
    };

    const onPointerCancel = (event) => {
      if (interactionRef.current.pointerId !== event.pointerId) return;
      if (mount.hasPointerCapture(event.pointerId)) {
        mount.releasePointerCapture(event.pointerId);
      }
      resetInteraction();
    };

    mount.addEventListener('pointerdown', onPointerDown);
    mount.addEventListener('pointermove', onPointerMove, { passive: false });
    mount.addEventListener('pointerup', onPointerUp);
    mount.addEventListener('pointercancel', onPointerCancel);

    return () => {
      mount.removeEventListener('pointerdown', onPointerDown);
      mount.removeEventListener('pointermove', onPointerMove);
      mount.removeEventListener('pointerup', onPointerUp);
      mount.removeEventListener('pointercancel', onPointerCancel);
      resetInteraction();
    };
  }, [count, goToNext, goToPrev, isMobile]);

  const handleClick = useCallback(
    (event) => {
      if (isMobile) return;
      if (interactionRef.current.didSwipe) {
        interactionRef.current.didSwipe = false;
        return;
      }
      if (!mountRef.current || !cameraRef.current) return;
      const rect = mountRef.current.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, cameraRef.current);
      const hits = raycaster.intersectObjects(cardMeshesRef.current);
      if (!hits.length) return;

      const clicked = hits[0].object.userData.index;
      if (clicked === focusedIndex) {
        openProductLink(products[focusedIndex]);
      } else {
        setFocusedIndex(clicked);
      }
    },
    [focusedIndex, isMobile, products]
  );

  if (count === 0) {
    return null;
  }

  const focusedProduct = products[focusedIndex];
  const focusedLink = normalizeProductLink(getProductLink(focusedProduct));
  const visitLabel = cardContents[focusedIndex]?.visitLabel || 'View product';

  return (
    <div
      className={`product-carousel-3d${isMobile ? ' product-carousel-3d--mobile' : ''}`}
      onMouseEnter={
        isMobile
          ? undefined
          : () => {
              if (!isPageScrollingRef.current) {
                setIsHovered(true);
              }
            }
      }
      onMouseLeave={
        isMobile
          ? undefined
          : () => {
              setIsHovered(false);
              mouseRef.current.set(0, 0);
            }
      }
    >
      <div className="product-carousel-3d__bg" aria-hidden="true" />

      <div
        className={`product-carousel-3d__mount${isDragging ? ' is-dragging' : ''}`}
        ref={mountRef}
        onMouseMove={isMobile ? undefined : handleMouseMove}
        onClick={isMobile ? undefined : handleClick}
        role="presentation"
      >
        {focusedLink ? (
          <div className="product-carousel-3d__cta-wrap" ref={ctaWrapRef}>
            <a
              href={focusedLink}
              className="product-carousel-3d__cta"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              aria-label={visitLabel}
            >
              {visitLabel}
              <span aria-hidden="true">→</span>
            </a>
          </div>
        ) : (
          <div className="product-carousel-3d__cta-wrap" ref={ctaWrapRef} aria-hidden="true" />
        )}
      </div>

      <div className="product-carousel-3d__fade product-carousel-3d__fade--top" aria-hidden="true" />
      <div className="product-carousel-3d__fade product-carousel-3d__fade--bottom" aria-hidden="true" />

      <button
        type="button"
        className="product-carousel-3d__nav product-carousel-3d__nav--prev"
        onClick={goToNext}
        aria-label="Next product"
      >
        <ChevronLeft />
      </button>
      <button
        type="button"
        className="product-carousel-3d__nav product-carousel-3d__nav--next"
        onClick={goToPrev}
        aria-label="Previous product"
      >
        <ChevronRight />
      </button>

      <div className="product-carousel-3d__dots" role="tablist" aria-hidden={isMobile}>
        {products.map((product, index) => (
          <button
            key={product.id}
            type="button"
            role="tab"
            aria-selected={index === focusedIndex}
            className={`product-carousel-3d__dot${index === focusedIndex ? ' is-active' : ''}`}
            onClick={
              isMobile
                ? undefined
                : (event) => {
                    event.stopPropagation();
                    setFocusedIndex(index);
                  }
            }
            tabIndex={isMobile ? -1 : 0}
            aria-label={cardContents[index]?.name || `Product ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductCarousel3D;
